import { useState } from 'react'

import { useRegionAnlegen, useRegionEntfernen, useRegionen, useSettings } from '../../hooks/queries'
import { useTexte } from '../../texte'
import { fehlertext } from '../../api/client'
import { rueckfrage } from '../../components/Rueckfrage'
import { holeOrt } from '../../lib/ortung'
import { Regionskarte } from './Regionskarte'

/**
 * Protected regions (#99, 1.54.0): where the route import lays no
 * suggestion. From use: "Privacy -- Heimatstandort soll nicht veröffentlicht
 * werden; diese Regionen werden häufig angefahren und sollen daher nicht
 * mit vielen verschiedenen POIs dargestellt werden."
 *
 * Under "POIs auf der Karte", next to the stop detection: both say what a
 * point is to a route. The list belongs to the administration and never
 * reaches a reader (`RegionsController`, `requireAdmin`); this card renders
 * only where the server answered, and the server answers only them.
 *
 * The centre comes from the map (a tap), from the device ("Hier") or from
 * two typed numbers -- three ways to the same two fields, so that nobody is
 * stuck with a map that starts at the world.
 */
export function RegionenCard() {
  const t = useTexte()
  const r = t.regionen
  const settings = useSettings()
  const regionen = useRegionen(settings.data?.ceiling !== undefined)
  const anlegen = useRegionAnlegen()
  const entfernen = useRegionEntfernen()
  const [name, setName] = useState('')
  const [radius, setRadius] = useState('3000')
  const [lat, setLat] = useState('')
  const [lon, setLon] = useState('')
  const [ortsproblem, setOrtsproblem] = useState<string | null>(null)
  const [ortet, setOrtet] = useState(false)

  // Without the ceiling this account is no administration -- the server decided.
  if (!settings.data?.ceiling || settings.data.map === undefined) return null
  if (regionen.isPending) return null
  if (regionen.isError) {
    return (
      <div className="card card--seite" data-regionen="">
        <h2 className="card__titel">{r.titel}</h2>
        <p className="notice" role="alert">{fehlertext(regionen.error)}</p>
      </div>
    )
  }
  const liste = regionen.data.regions
  const grenzen = regionen.data.limits
  const latZahl = Number(lat)
  const lonZahl = Number(lon)
  const mitte = lat !== '' && lon !== '' && Number.isFinite(latZahl) && Number.isFinite(lonZahl) ? { lat: latZahl, lon: lonZahl } : null
  const radiusZahl = Number(radius)
  const bereit = mitte !== null && name.trim() !== '' && Number.isInteger(radiusZahl) && radiusZahl >= grenzen.radius_m.min && radiusZahl <= grenzen.radius_m.max

  return (
    <div className="card card--seite" data-regionen={liste.length}>
      <h2 className="card__titel">{r.titel}</h2>
      <p className="card__lead">{r.lead}</p>

      {liste.length === 0 ? (
        <p className="feld__hinweis klein">{r.leer}</p>
      ) : (
        <ul className="hinweisliste" data-regionen-liste="">
          {liste.map((region) => (
            <li key={region.id} className="regionen__zeile" data-region={region.id}>
              <span>{r.zeile(region.name, region.radius_m)}</span>
              <button
                type="button"
                className="button button--outline button--small"
                disabled={entfernen.isPending}
                onClick={async () => {
                  if (await rueckfrage(r.entfernenFrage(region.name), r.entfernen)) entfernen.mutate(region.id)
                }}
              >
                {r.entfernen}
              </button>
            </li>
          ))}
        </ul>
      )}
      {entfernen.isError && <p className="notice" role="alert">{fehlertext(entfernen.error)}</p>}

      <p className="zwischenzeile">{r.neu}</p>
      <Regionskarte
        quelle={settings.data.map}
        regionen={liste}
        mitte={mitte}
        radius={Number.isFinite(radiusZahl) ? radiusZahl : 0}
        onWahl={(neuLat, neuLon) => {
          setLat(neuLat.toFixed(6))
          setLon(neuLon.toFixed(6))
        }}
      />
      <p className="feld__hinweis klein">{r.karteHinweis}</p>

      <div className="row row--eng row--luft">
        <button
          type="button"
          className="button button--outline button--small"
          disabled={ortet}
          onClick={async () => {
            setOrtet(true)
            setOrtsproblem(null)
            try {
              const ort = await holeOrt()
              setLat(ort.lat.toFixed(6))
              setLon(ort.lon.toFixed(6))
            } catch (fehler) {
              setOrtsproblem(fehlertext(fehler))
            } finally {
              setOrtet(false)
            }
          }}
        >
          {/* The same words as the chat's weather tap (Codereview E7): one sentence for one thing. */}
          {ortet ? t.chat.wetterOrtung : r.hier}
        </button>
      </div>
      {ortsproblem !== null && <p className="feld__hinweis feld__hinweis--zuviel" role="alert">{ortsproblem}</p>}

      <div className="row row--eng">
        <label className="field field--luft field--halb">
          <span className="field__label">{r.lat}</span>
          <input className="field__input" type="text" inputMode="decimal" name="region_lat" value={lat} onChange={(e) => setLat(e.target.value)} />
        </label>
        <label className="field field--luft field--halb">
          <span className="field__label">{r.lon}</span>
          <input className="field__input" type="text" inputMode="decimal" name="region_lon" value={lon} onChange={(e) => setLon(e.target.value)} />
        </label>
      </div>
      <label className="field field--luft">
        <span className="field__label">{r.name}</span>
        <input className="field__input" type="text" name="region_name" maxLength={grenzen.name} value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="field field--luft">
        <span className="field__label">{r.radius}</span>
        <input
          className="field__input"
          type="number"
          inputMode="numeric"
          name="region_radius"
          value={radius}
          min={grenzen.radius_m.min}
          max={grenzen.radius_m.max}
          onChange={(e) => setRadius(e.target.value)}
        />
        <span className="feld__hinweis">{r.radiusHinweis(grenzen.radius_m.min, grenzen.radius_m.max)}</span>
      </label>

      {anlegen.isError && <p className="notice" role="alert">{fehlertext(anlegen.error)}</p>}
      <button
        type="button"
        className="button button--outline"
        disabled={!bereit || anlegen.isPending}
        data-region-anlegen=""
        onClick={() =>
          anlegen.mutate(
            { name: name.trim(), lat: latZahl, lon: lonZahl, radius_m: radiusZahl },
            { onSuccess: () => { setName(''); setLat(''); setLon('') } },
          )
        }
      >
        {anlegen.isPending ? t.allgemein.laeuft : r.anlegen}
      </button>
    </div>
  )
}
